import axiosInstance from 'config/axiosInstance';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { businessRangeQuery, formatBusiness } from 'config/time';
import { PAGE_SIZE } from 'config/constant';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DateRangeFilter from 'reusable/datefilterrange';
import Export from 'reusable/export';
import { CsvCondition, csvDateTime, deviceTimeZoneExportFields } from 'config/csv';
import DeviceTimeZone, { deviceTimeZoneOf } from 'reusable/DeviceTimeZone';
import Pagination from 'reusable/pagination';
import SortBy from 'reusable/sortby';
import { useTranslation } from 'react-i18next';


interface DriverHealthCheckProps {
  id?: string;
}

const DriverHealthCheckTable: React.FC<DriverHealthCheckProps> = ({ id }) => {
  const { t, i18n } = useTranslation('driver');
  // 既定は新しい順（サーバーの並び順）。もう一方は記録の時刻の古い順。
  const sortingOptions = [
    { label: t('sort.default', { ns: 'common' }), value: '' },
    { label: t('sort.oldest', { ns: 'common' }), value: 'create_time' },
  ];
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortValue, setSortValue] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [driverHealthCheck, setDriverHealthCheck] = useState<any[]>([]);

  const formatDateTime = (dateTimeString: string) => ({
    // EV-116。端末の時間帯ではなく、業務の時間帯（日本時間）で出す（絞り込みの業務日と揃える）。
    time: formatBusiness(dateTimeString, 'HH:mm'),
    formattedDate: formatBusiness(dateTimeString, i18n.language === 'jp' ? 'yyyy/MM/dd' : 'dd MMM yyyy'),
  });

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
    // 絞り込みを変えたら1ページ目から表示する（後ろのページのままだと空になる）。
    setCurrentPage(1);
  };

  const exportFields = [
    // 健康点検の記録の項目。測定値・測定時刻（data / testing_time）はこの記録に無い。
    // ADM-11-AC1。行を画面・API の記録へたどれるよう、記録の ID を先頭に出す。
    { header: 'Record ID', field: 'id' },
    { header: 'Driver Name', field: 'user.name' },
    { header: 'Driver Email', field: 'user.email' },
    { header: 'Inspection Date', field: 'date' },
    { header: 'Maintenance Manager', field: 'maintenance_manager' },
    { header: 'Operations Manager', field: 'operations_manager' },
    // ADM-05。作成日時は日本時間で出す（UTC の ISO 文字列のままだった）。
    { header: 'Date & Time', field: 'create_time', customExport: csvDateTime },
    // EV-116 段階3。送信の時点の端末の時間帯（無い記録は空欄）。
    ...deviceTimeZoneExportFields(),
  ];
  // ADM-11-AC2。出力した条件をファイル名に入れる（表示中のページを出す）。
  const exportConditions: CsvCondition[] = [
    ['period', dateRange.startDate && dateRange.endDate ? `${dateRange.startDate}_${dateRange.endDate}` : ''],
    ['driver', id],
    ['sort', sortValue],
    ['page', currentPage],
  ];

  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      let apiUrl = '/v1/health_inspection_records/?';
      if (dateRange.startDate && dateRange.endDate) {
        // 健康点検の期間は作成日時。check_in はこの記録に無く、無視されていた。
        apiUrl += businessRangeQuery('create_time', dateRange.startDate, dateRange.endDate);
      }
      // 並び替えは選んだ値を問い合わせに渡す。以前は選択肢だけで反映されていなかった。
      apiUrl += `order_by=${sortValue}&page=${currentPage}&page_size=${PAGE_SIZE}&user=${id}`;
      const response = await axiosInstance.get(apiUrl);
      if (!isCurrent()) return;
      setFetchError(null);
      setCount(response.data.count);
      setDriverHealthCheck(response.data.results);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Failed to fetch data', error);
      setDriverHealthCheck([]);
      setFetchError(error);
    }
  }, [id, currentPage, dateRange, sortValue, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleDetailsClick = (id: string) => {
    navigate(`/driver/healthcheck/${id}`);
  };

  return (
    <section className="flex flex-col space-y-2">
      <div className="flex flex-row space-x-2 items-center justify-between">
        {/* ToolBar Section */}
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
        <div className="flex flex-row space-x-2">
          <DateRangeFilter onDateRangeChange={handleDateRangeChange} />
          {/* ADM-11。CSV には表示中の記録を出す。以前は仮の1行だった。 */}
          <Export
            data={driverHealthCheck}
            exportFields={exportFields}
            name="driver_health_check"
            conditions={exportConditions}
          />
        </div>
      </div>
      {/* Table */}
      <div>
        <div className="flex flex-row bg-primary/50 rounded-md py-2 px-4">
          <h6 className="flex-1 text-center">
            {t('view.health-check.submit-date')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.health-check.submit-time')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.health-check.status')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.health-check.detail')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('time-zone.device', { ns: 'common' })}
          </h6>
        </div>
        <div>
          {fetchError ? <FetchFailed error={fetchError} onRetry={fetchData} /> : null}
          {driverHealthCheck.map((check, index) => {
            const { time, formattedDate } = formatDateTime(check.create_time);
            const summary = healthCheckSummary(check.details?.driver_healthcheck?.[0]?.item);
            return (
              <div key={index} className="flex flex-row py-2 px-4 border-b">
                <p className="flex-1 text-center">{formattedDate}</p>
                <p className="flex-1 text-center">{time}</p>
                <div className="flex flex-1 items-center justify-center">
                  <p
                    className={`w-1/3 text-center px-6 py-1 rounded-md ${
                      summary.allOk
                        ? 'text-green-500 bg-gray-100'
                        : 'text-red-500 bg-red-100'
                    }`}
                  >
                    {summary.ok}/{summary.total}
                  </p>
                </div>
                <button
                  type="button"
                  className="flex-1 text-center underline text-primary/300"
                  onClick={() => handleDetailsClick(check.id)}
                >
                  {t('view.health-check.detail_button')}
                </button>
                {/* EV-116 段階3。送信の時点の端末の時間帯。 */}
                <DeviceTimeZone className="flex-1 text-center" {...deviceTimeZoneOf(check)} at={check.create_time} />
              </div>
            );
          })}
        </div>
      </div>
      {/* Pagination Section */}
      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </section>
  );
};

/**
 * 健康点検の一覧の「OK の数／設問の数」。設問の数は記録ごとに数える（2026-10-19。以前は 8 で固定で、
 * 体温の行（アプリが name・status・note で送る）が増えると健康な提出が 9/8 で赤、発熱でも 8/8 で緑になった）。
 * 以前のアプリの記録は体温を { 設問: "36.5" }（status なし）で保存していたので、分母は status が真偽の
 * 設問だけで数える（古い健康な記録を 8/9 にしない）。すべての設問が OK のときだけ allOk。
 */
export function healthCheckSummary(items: Array<{ status?: unknown }> | undefined) {
  const list = (Array.isArray(items) ? items : []).filter(
    (item) => typeof item?.status === 'boolean'
  );
  const ok = list.filter((item) => item.status === true).length;
  return { ok, total: list.length, allOk: list.length > 0 && ok === list.length };
}

export default DriverHealthCheckTable;
